import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useParams } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft, Trophy, ChevronUp, Share2, Copy, Check,
  ExternalLink, Megaphone, Users, DollarSign, Image, FileText, Video, Link2, Hash
} from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";

export default function SubmissionEntry() {
  const params = useParams<{ slug: string; shareToken: string }>();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = trpc.game.getSubmissionByToken.useQuery(
    { shareToken: params.shareToken || "" },
    { enabled: !!params.shareToken }
  );

  const voteMutation = trpc.game.vote.useMutation();
  const utils = trpc.useUtils();

  const submission = data?.submission;
  const game = data?.game;

  const handleVote = async () => {
    if (!submission) return;
    try {
      await voteMutation.mutateAsync({
        submissionId: submission.id,
        referralShareToken: params.shareToken,
      });
      toast.success("Vote recorded! Thank you!");
      utils.game.getSubmissionByToken.invalidate({ shareToken: params.shareToken });
    } catch (err: any) {
      if (err?.message?.includes("already voted")) {
        toast.error("You've already voted for this submission");
      } else {
        toast.error("Failed to vote. Please try again.");
      }
    }
  };

  const shareUrl = typeof window !== "undefined"
    ? window.location.href
    : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const prizePool = useMemo(() => {
    if (!game) return 0;
    // 15% platform fee deducted, then host and promoter cuts from remainder
    const total = (game.entryFee / 100) * game.maxPlayers;
    const winnerPct = (100 - 15 - (game.hostCut || 10) - (game.promoterCut || 10)) / 100;
    return Math.floor(total * winnerPct);
  }, [game]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="w-5 h-5 border-2 border-neon border-t-transparent rounded-full animate-spin" />
          Loading submission...
        </div>
      </div>
    );
  }

  if (!submission || !game) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold">Submission Not Found</h2>
          <Link href="/">
            <Button variant="outline">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href={`/game/${game.slug}`} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">All Submissions</span>
          </Link>
          <Badge
            className={`text-xs ${
              game.status === "active" ? "bg-neon/20 text-neon border-neon/30" :
              game.status === "voting" ? "bg-gold/20 text-gold border-gold/30" :
              "bg-secondary text-secondary-foreground"
            }`}
            variant="outline"
          >
            {game.status.toUpperCase()}
          </Badge>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid md:grid-cols-5 gap-8"
        >
          {/* Submission Content — Large Display */}
          <div className="md:col-span-3">
            <div className="glass-panel rounded-xl overflow-hidden">
              {submission.submissionType === "image" ? (
                <div className="aspect-square bg-secondary/50 flex items-center justify-center">
                  <img
                    src={submission.content}
                    alt={submission.caption || "Submission"}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : submission.submissionType === "video" ? (
                <div className="aspect-video bg-secondary/50">
                  <video src={submission.content} controls className="w-full h-full" />
                </div>
              ) : (
                <div className="p-8 min-h-[300px] flex items-center justify-center bg-secondary/20">
                  <p className="text-lg text-foreground/90 text-center max-w-md leading-relaxed">
                    {submission.content}
                  </p>
                </div>
              )}

              {submission.caption && (
                <div className="p-4 border-t border-border/50">
                  <p className="text-sm text-muted-foreground">{submission.caption}</p>
                </div>
              )}
            </div>
          </div>

          {/* Vote & Share Panel */}
          <div className="md:col-span-2 space-y-4">
            {/* Vote CTA — Big and Prominent */}
            <div className="glass-panel rounded-xl p-6 text-center glow-neon">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Current Votes</p>
              <p className="text-5xl font-bold font-mono text-neon mb-4">{submission.voteCount}</p>
              <Button
                size="lg"
                className="w-full bg-neon hover:bg-neon-light text-background font-semibold gap-2 h-12"
                onClick={handleVote}
                disabled={voteMutation.isPending}
              >
                <ChevronUp className="w-5 h-5" />
                {voteMutation.isPending ? "Voting..." : "Vote for This Entry"}
              </Button>
            </div>

            {/* Game Info */}
            <div className="glass-panel rounded-xl p-5 space-y-3">
              <h3 className="font-semibold text-sm text-neon">{game.title}</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5" /> Prize Pool
                  </span>
                  <span className="font-mono font-bold text-gold">${prizePool.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Max Players
                  </span>
                  <span className="font-mono">{game.maxPlayers.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5" /> Entry Fee
                  </span>
                  <span className="font-mono">${(game.entryFee / 100).toFixed(2)}</span>
                </div>
              </div>
              <Link href={`/game/${game.slug}`}>
                <Button variant="outline" size="sm" className="w-full mt-2 gap-1.5">
                  <Trophy className="w-3.5 h-3.5" />
                  View All Entries
                </Button>
              </Link>
            </div>

            {/* Share This Submission */}
            <div className="glass-panel rounded-xl p-5 space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Share This Entry</p>
              <p className="text-xs text-muted-foreground">
                Help this entry win by sharing! New players who join through this link earn the sharer promoter rewards.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5 border-gold/30 hover:bg-gold/10 hover:border-gold/50 hover:text-gold"
                onClick={copyLink}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied!" : "Copy Share Link"}
              </Button>
            </div>

            {/* Influencer Channel */}
            {game.influencerChannelUrl && (
              <a
                href={game.influencerChannelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-panel rounded-xl p-5 flex items-center gap-3 hover:border-gold/30 transition-colors group block"
              >
                <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                  <Megaphone className="w-5 h-5 text-gold" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Hosted by</p>
                  <p className="text-sm font-medium text-gold group-hover:text-gold-light truncate">
                    Visit Creator's Channel
                  </p>
                </div>
                <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0 ml-auto" />
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
